class WooCommerceAPI {
  constructor() {
    this.cache = new Map()
  }

  getBaseURL() {
    if (typeof window === 'undefined') {
      // Server-side (SSR): ga direct via localhost, geen TLS-round-trip via nginx
      const port = process.env.PORT || 3000
      return `http://localhost:${port}/api/wc/v3`
    }
    // Client-side: relatieve URL → zelfde host als de browser
    return '/api/wc/v3'
  }

  async request(endpoint, options = {}) {
    const baseURL = this.getBaseURL()
    const url = `${baseURL}/${endpoint}`

    // Fail-fast timeout zodat een trage/verzadigde backend de pagina niet laat
    // hangen (client krijgt dan gewoon [] i.p.v. een oneindige fetch).
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timer = ac ? setTimeout(() => ac.abort(), options.timeout || 15000) : null

    try {
      const res = await fetch(url, {
        ...options,
        signal: ac ? ac.signal : undefined,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      })

      if (!res.ok) {
        const err = await res.text()
        throw new Error(err || res.statusText)
      }

      const data = await res.json()
      return this.simplify(data)
    } catch (err) {
      console.error('❌ Woo error:', url, err?.name === 'AbortError' ? 'timeout' : err)
      return []
    } finally {
      if (timer) clearTimeout(timer)
    }
  }

  // Raw request zonder simplify - voor orders/coupons
  async rawRequest(endpoint, options = {}) {
    const baseURL = this.getBaseURL()
    const url = `${baseURL}/${endpoint}`
    const res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    })
    const data = await res.json()
    if (!res.ok) {
      const msg = data?.message || data || res.statusText
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
    }
    return data
  }

  /**
   * Maak een WooCommerce bestelling aan.
   * @param {Object} payload  – WC order body (billing, shipping, line_items, etc.)
   * @returns {Object}        – ruwe WC order response
   */
  createOrder(payload) {
    return this.rawRequest('orders', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }

  /**
   * Haal een specifieke order op (voor de thank-you pagina).
   */
  getOrder(orderId) {
    return this.rawRequest(`orders/${orderId}`)
  }

  rewriteImageUrl(url) {
    if (!url) return null
    return url.replace('https://admin.plug2charge.nl', '')
  }

  /**
   * Decodeer HTML-entities in tekstvelden (naam, SKU, …).
   * WooCommerce slaat sommige velden HTML-gecodeerd op en geeft dan
   * bijv. "Plug &amp; Play" terug - dit maakt er "Plug & Play" van.
   * Werkt zowel server-side (Node) als client-side (browser).
   */
  decodeHtml(str) {
    if (!str) return str
    return String(str)
      .replace(/&amp;/g,  '&')
      .replace(/&lt;/g,   '<')
      .replace(/&gt;/g,   '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&nbsp;/g, ' ')
  }

  simplify(data) {
    const mapProduct = (p) => ({
      id: p.id,
      name: this.decodeHtml(p.name),
      slug: p.slug,
      sku: this.decodeHtml(p.sku),
      price: p.price,
      regular_price: p.regular_price,
      sale_price: p.sale_price,
      on_sale: p.on_sale,
      featured: p.featured,
      total_sales: parseInt(p.total_sales || 0, 10),
      short_description: p.short_description,
      description: p.description,
      images: (p.images || []).map(img => ({
        ...img,
        src: this.rewriteImageUrl(img.src)
      })),
      thumbnail: this.rewriteImageUrl(p.images?.[0]?.src),
      categories: (p.categories || []).map(cat => ({
        ...cat,
        name: this.decodeHtml(cat.name),
      })),
      tags: (p.tags || []).map(tag => ({
        id:   tag.id,
        name: this.decodeHtml(tag.name),
        slug: tag.slug,
      })),
      attributes: p.attributes || [],
      average_rating: p.average_rating,
      rating_count: p.rating_count,
      manage_stock: p.manage_stock,
      stock_quantity: p.stock_quantity,
      stock_status: p.stock_status,
      in_stock: p.stock_status === 'instock',
      backorders_allowed: !!p.backorders_allowed,
      date_created: p.date_created,
      date_on_sale_to: p.date_on_sale_to || null,
      // GTIN/EAN + MPN voor rich results. WC 9.2+ zet GTIN in global_unique_id;
      // oudere installs/plugins gebruiken meta_data. Alleen doorgeven als aanwezig.
      gtin: (p.global_unique_id ||
        (p.meta_data || []).find(m => ['gtin', '_gtin', 'gtin13', '_gtin13', 'ean', '_ean', 'barcode', '_barcode', 'hwp_var_gtin'].includes(m.key))?.value ||
        '') || null,
      mpn: ((p.meta_data || []).find(m => ['mpn', '_mpn', '_wpm_gtin_code'].includes(m.key))?.value || '') || null,
      weight: p.weight,
      dimensions: p.dimensions,
      // Datasheets: ondersteunt WC Downloads én custom meta velden
      downloads: (p.downloads || []).map(d => ({
        id:   d.id,
        name: d.name,
        file: d.file,
      })),
      // Handige shortcuts uit meta_data (direct beschikbaar als property)
      featured_headline: this.decodeHtml((p.meta_data || []).find(m => m.key === 'featured_headline')?.value || null),
      featured_subtitle: this.decodeHtml((p.meta_data || []).find(m => m.key === 'featured_subtitle')?.value || null),

      // USPs: custom veld 'usps' gescheiden door | bijv. "Morgen in huis|2 jaar garantie|Gratis verzending"
      // Leeg → null (component valt terug op site-brede standaard USPs)
      usps: (() => {
        const raw = (p.meta_data || []).find(m => m.key === 'usps')?.value || ''
        if (!raw) return null
        return raw.split('|').map(s => s.trim()).filter(Boolean)
      })(),

      // Product type + grouped sub-products
      type:             p.type || 'simple',
      grouped_products: p.grouped_products || [],
      price_html:       p.price_html || '',

      // Bundle: is_bundle meta + bundle_items voor producten + aantallen
      // Custom Fields:
      //   is_bundle    = 1
      //   bundle_items = 123:2|456:1|789:3  (product_id:aantal, gescheiden door |)
      // Zonder bundle_items valt het terug op upsell_ids (allemaal qty 1)
      is_bundle: !!(p.meta_data || []).find(m => (m.key === 'is_bundle' || m.key === '_is_bundle') && m.value == '1'),

      // Parsed bundle items: [{ id, qty }]
      bundle_items: (() => {
        const raw = (p.meta_data || []).find(m => m.key === 'bundle_items')?.value || ''
        if (raw) {
          return raw.split('|').map(part => {
            const [id, qty] = part.trim().split(':')
            return { id: parseInt(id), qty: parseInt(qty) || 1 }
          }).filter(b => b.id)
        }
        // Fallback: upsell_ids allemaal qty 1
        return (p.upsell_ids || []).map(id => ({ id, qty: 1 }))
      })(),

      // Linked products (worden ingesteld via WooCommerce → Gelinkte producten)
      cross_sell_ids: p.cross_sell_ids || [],
      related_ids:    p.related_ids || [],
      upsell_ids:     p.upsell_ids || [],

      // meta_data gefilterd: alleen publieke / nuttige sleutels doorlaten
      // (sleutels zonder _ zijn altijd publiek; underscore-sleutels alleen op allowlist)
      meta_data: (p.meta_data || [])
        .filter(m => !m.key.startsWith('_') ||
          ['_datasheet', '_datasheet_url', '_datasheet_file', '_is_bundle'].includes(m.key))
        .map(m => ({ key: m.key, value: m.value })),
    })

    if (Array.isArray(data)) return data.map(mapProduct)
    return mapProduct(data)
  }

  getProducts(params = {}) {
    return this.request(`products?${new URLSearchParams(params)}`)
  }

  getProduct(id) {
    return this.request(`products/${id}`)
  }

  async getProductBySlug(slug) {
    const res = await this.request(`products?slug=${slug}`)
    return res?.[0] || null
  }

  async getBrandBySlug(slug) {
    const data = await this.rawRequest(`products/brands?slug=${encodeURIComponent(slug)}`)
    const b = data?.[0]
    return b ? { ...b, name: this.decodeHtml(b.name), description: this.decodeHtml(b.description) } : null
  }

  async getCategories(params = {}) {
    const qs   = new URLSearchParams({ per_page: 100, hide_empty: true, ...params })
    const data = await this.rawRequest(`products/categories?${qs}`)
    // rawRequest slaat simplify() over, dus HTML-entities handmatig decoderen
    return data.map(cat => ({
      ...cat,
      name:        this.decodeHtml(cat.name),
      description: this.decodeHtml(cat.description),
    }))
  }

  /**
   * Haal meerdere producten op via hun ID's (voor cross-sells / upsells).
   */
  async getProductsByIds(ids) {
    if (!ids?.length) return []
    return this.request(`products?include=${ids.join(',')}&per_page=${Math.min(ids.length, 20)}`)
  }

  /**
   * Haal reviews op voor één product.
   * @param {number} productId  - WooCommerce product-ID
   */
  getReviews(productId) {
    return this.rawRequest(`products/reviews?product=${productId}&per_page=20&status=approved`)
  }
}

export default new WooCommerceAPI()